import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  ThermometerSnowflake,
  Droplets,
  Wind,
  Power,
  CircleAlert,
  Zap,
  Volume2,
  Biohazard,
  Check,
  Loader2,
  CircleCheck,
  TriangleAlert,
} from "lucide-react";
import Api from "../Api";
import dayjs from "dayjs";
import Logo from "../assets/logo.png";

const JENIS_PERAWATAN = [
  { value: "insidental", label: "Insidental" },
  { value: "rutin", label: "Rutin" },
  { value: "penggantian", label: "Penggantian" },
];

const KELUHAN_AWAL = [
  { label: "AC Tidak Dingin / Kurang Dingin", icon: ThermometerSnowflake },
  { label: "Bocor Air Indoor", icon: Droplets },
  { label: "Hembusan Angin Lemah", icon: Wind },
  { label: "Mati Total", icon: Power },
  { label: "Indikator Error / Berkedip", icon: CircleAlert },
  { label: "MCB Turun / Korslet", icon: Zap },
  { label: "Suara Berisik / Kasar", icon: Volume2 },
  { label: "Bau Tidak Sedap", icon: Biohazard },
];

const STATUS_AKHIR = [
  { value: "Normal", label: "Normal" },
  { value: "Menunggu Sparepart", label: "Menunggu Sparepart" },
  { value: "Dalam Pengerjaan", label: "Dalam Pengerjaan" },
];

const MAX_PERAWATAN_AC_PER_HARI = 3;

const initialForm = {
  jenis_perawatan: "",
  keluhan_awal: "",
  tindakan: "",
  penggantian_sparepart: "",
  status_akhir: "",
  catatan: "",
};

const inputClass =
  "w-full border-0 sm:border sm:border-gray-200 bg-gray-50 sm:bg-white rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-500 sm:focus:border-transparent";

function PerawatanAc() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const [pengirimanHariIni, setPengirimanHariIni] = useState(0);
  const [lastSubmittedData, setLastSubmittedData] = useState(null);

  const tanggalPerawatan = dayjs().format("YYYY-MM-DD");
  const sisaPengiriman = Math.max(0, MAX_PERAWATAN_AC_PER_HARI - pengirimanHariIni);

  const getKode = async () => {
    const kode = encodeURIComponent(id);
    try {
      let res;
      try {
        res = await Api.get(`/perawatan-ac/${kode}`);
      } catch {
        res = await Api.get(`/inventori/${kode}`);
      }

      if (res.data?.data) {
        setData(res.data.data);
      } else {
        setError("Kode inventaris tidak ditemukan.");
      }
    } catch (err) {
      setError(
        err.response?.data?.message || "Gagal memuat data inventaris."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getKode();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "jenis_perawatan" && value !== "penggantian") {
        updated.penggantian_sparepart = "";
      }
      return updated;
    });
    setError("");
    setShowSuccessModal(false);
    setShowLimitModal(false);
  };

  const showSparepart = form.jenis_perawatan === "penggantian";

  const selectedKeluhan = form.keluhan_awal
    ? form.keluhan_awal.split(", ").filter(Boolean)
    : [];

  const toggleKeluhan = (label) => {
    setForm((prev) => {
      const current = prev.keluhan_awal
        ? prev.keluhan_awal.split(", ").filter(Boolean)
        : [];
      const next = current.includes(label)
        ? current.filter((item) => item !== label)
        : [...current, label];
      return { ...prev, keluhan_awal: next.join(", ") };
    });
    setError("");
    setShowSuccessModal(false);
    setShowLimitModal(false);
  };

  const buildPayload = () => ({
    kode: data?.kode,
    jenis_perawatan: form.jenis_perawatan,
    keluhan_awal: form.keluhan_awal || null,
    tindakan: form.tindakan || null,
    penggantian_sparepart: showSparepart ? form.penggantian_sparepart || null : null,
    tanggal_perawatan: tanggalPerawatan,
    status_akhir: form.status_akhir || null,
    teknisi: null,
    catatan: form.catatan || null,
  });

  const validateForm = () => {
    if (!data?.kode) {
      setError("Kode inventaris wajib diisi.");
      return false;
    }
    if (!form.jenis_perawatan) {
      setError("Jenis perawatan wajib dipilih.");
      return false;
    }
    if (!tanggalPerawatan) {
      setError("Tanggal perawatan wajib diisi.");
      return false;
    }
    if (limitReached || pengirimanHariIni >= MAX_PERAWATAN_AC_PER_HARI) {
      setError(
        `Pengiriman perawatan untuk kode ${data.kode} pada tanggal yang sama sudah mencapai batas ${MAX_PERAWATAN_AC_PER_HARI} kali.`
      );
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setShowLimitModal(false);

    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const res = await Api.post("/perawatan-ac", buildPayload());
      const totalBaru = pengirimanHariIni + 1;
      setLastSubmittedData(res.data?.data || null);
      setPengirimanHariIni(totalBaru);
      if (totalBaru >= MAX_PERAWATAN_AC_PER_HARI) {
        setLimitReached(true);
      }
      setShowSuccessModal(true);
      setForm(initialForm);
    } catch (err) {
      const status = err.response?.status;
      const message =
        err.response?.data?.message || "Gagal menyimpan data perawatan AC.";

      if (status === 429) {
        setLastSubmittedData(err.response?.data?.data || null);
        setPengirimanHariIni(MAX_PERAWATAN_AC_PER_HARI);
        setLimitReached(true);
        setError(message);
        setShowLimitModal(true);
      } else {
        setError(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const formatJenisPerawatan = (value) =>
    JENIS_PERAWATAN.find((item) => item.value === value)?.label || value;

  const renderSubmittedSummary = (item) => {
    if (!item) return null;
    return (
      <div className="mt-4 text-left bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
        <div className="flex justify-between gap-2">
          <span className="text-gray-500">Jenis</span>
          <span className="text-gray-900 font-medium text-right">
            {formatJenisPerawatan(item.jenis_perawatan)}
          </span>
        </div>
        {item.keluhan_awal && (
          <div className="flex justify-between gap-2">
            <span className="text-gray-500 shrink-0">Keluhan</span>
            <span className="text-gray-900 text-right">{item.keluhan_awal}</span>
          </div>
        )}
        {item.status_akhir && (
          <div className="flex justify-between gap-2">
            <span className="text-gray-500">Status</span>
            <span className="text-gray-900 font-medium">{item.status_akhir}</span>
          </div>
        )}
        {item.tanggal_perawatan && (
          <div className="flex justify-between gap-2">
            <span className="text-gray-500">Tanggal</span>
            <span className="text-gray-900">
              {dayjs(item.tanggal_perawatan).format("DD MMM YYYY")}
            </span>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white sm:bg-gradient-to-br sm:from-blue-50 sm:to-indigo-100 flex items-center justify-center px-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-red-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Memuat data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white sm:bg-gradient-to-br sm:from-blue-50 sm:to-indigo-100">
      <div className="w-full max-w-md mx-auto sm:px-4 sm:py-6">
        <div className="bg-white min-h-screen sm:min-h-0 sm:rounded-2xl sm:shadow-lg sm:border sm:border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-red-500 to-red-600 px-4 sm:px-5 py-4">
            <img src={Logo} alt="Logo" className="w-[120px] h-8 mb-3" />
            <h1 className="text-lg font-bold text-white">Form Perawatan AC</h1>
            {data?.merk && (
              <p className="text-red-100 text-sm mt-1">{data.merk}</p>
            )}
          </div>

          {!data ? (
            <div className="p-4 sm:p-6 text-center">
              {error && (
                <div className="bg-red-50 sm:border sm:border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 mb-4 text-left">
                  {error}
                </div>
              )}
              <p className="text-gray-500">Data inventaris tidak ditemukan.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-4 sm:p-5 space-y-4 pb-8">
              {error && (
                <div className="bg-red-50 sm:border sm:border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">
                  {error}
                </div>
              )}

              {!limitReached && pengirimanHariIni > 0 && (
                <div className="bg-blue-50 sm:border sm:border-blue-200 text-blue-700 text-sm rounded-xl px-4 py-3">
                  Pengiriman hari ini: {pengirimanHariIni}/{MAX_PERAWATAN_AC_PER_HARI}.
                  Sisa {sisaPengiriman} kali.
                </div>
              )}

              {limitReached && (
                <div className="bg-amber-50 sm:border sm:border-amber-200 text-amber-800 text-sm rounded-xl px-4 py-3">
                  Batas pengiriman harian tercapai ({MAX_PERAWATAN_AC_PER_HARI} kali per hari untuk kode yang sama).
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Kode
                </label>
                <input
                  type="text"
                  value={data.kode || ""}
                  readOnly
                  className="w-full bg-gray-100 sm:border sm:border-gray-200 border-0 rounded-xl px-4 py-3 text-gray-700 text-sm cursor-not-allowed"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Jenis Perawatan <span className="text-red-500">*</span>
                </label>
                <select
                  name="jenis_perawatan"
                  value={form.jenis_perawatan}
                  onChange={handleChange}
                  className={inputClass}
                >
                  <option value="">Pilih jenis perawatan</option>
                  {JENIS_PERAWATAN.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Keluhan Awal
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Ketuk untuk memilih keluhan (bisa lebih dari satu)
                </p>
                <div className="grid grid-cols-1 gap-2">
                  {KELUHAN_AWAL.map((item) => {
                    const isSelected = selectedKeluhan.includes(item.label);
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.label}
                        type="button"
                        onClick={() => toggleKeluhan(item.label)}
                        className={`flex items-center gap-3 w-full text-left px-4 py-3 rounded-xl text-sm transition-all duration-200 ${
                          isSelected
                            ? "bg-red-50 text-red-800 sm:border sm:border-red-500 sm:shadow-sm"
                            : "bg-gray-50 text-gray-700 sm:border sm:border-gray-200 sm:bg-white hover:bg-red-50/50 sm:hover:border-red-300"
                        }`}
                      >
                        <span
                          className={`shrink-0 p-1.5 rounded-lg ${
                            isSelected ? "bg-red-100 text-red-600" : "bg-white text-gray-500"
                          }`}
                        >
                          <Icon className="w-4 h-4" strokeWidth={2} />
                        </span>
                        <span className="flex-1 font-medium">{item.label}</span>
                        <span
                          className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                            isSelected
                              ? "bg-red-500 text-white"
                              : "bg-white sm:border-2 sm:border-gray-300"
                          }`}
                        >
                          {isSelected && <Check className="w-3 h-3" strokeWidth={3} />}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {selectedKeluhan.length > 0 && (
                  <p className="text-xs text-red-600 mt-2 font-medium">
                    {selectedKeluhan.length} keluhan dipilih
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Tindakan
                </label>
                <textarea
                  name="tindakan"
                  value={form.tindakan}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Tindakan yang dilakukan..."
                  className={`${inputClass} resize-none`}
                />
              </div>

              {showSparepart && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Penggantian Sparepart
                  </label>
                  <input
                    type="text"
                    name="penggantian_sparepart"
                    value={form.penggantian_sparepart}
                    onChange={handleChange}
                    placeholder="Nama sparepart yang diganti"
                    className={inputClass}
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Status Akhir
                </label>
                <select
                  name="status_akhir"
                  value={form.status_akhir}
                  onChange={handleChange}
                  className={inputClass}
                >
                  <option value="">Pilih status akhir</option>
                  {STATUS_AKHIR.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Catatan
                </label>
                <textarea
                  name="catatan"
                  value={form.catatan}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Catatan tambahan..."
                  className={`${inputClass} resize-none`}
                />
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={submitting || limitReached}
                  className="w-full bg-red-600 hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed text-white font-semibold py-3.5 px-4 rounded-xl transition-colors duration-200"
                >
                  {submitting
                    ? "Menyimpan..."
                    : limitReached
                    ? "Batas Pengiriman Tercapai"
                    : "Simpan Perawatan"}
                </button>
                {!limitReached && (
                  <p className="text-xs text-gray-400 text-center mt-2">
                    Maksimal {MAX_PERAWATAN_AC_PER_HARI} kali pengiriman per hari untuk kode yang sama
                  </p>
                )}
              </div>
            </form>
          )}
        </div>
      </div>

      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden max-h-[90vh] overflow-y-auto">
            <div className="px-6 pt-8 pb-6 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CircleCheck className="w-9 h-9 text-green-600" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                Berhasil Dikirim
              </h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Data perawatan AC untuk{" "}
                <span className="font-semibold text-gray-700">{data?.kode}</span>{" "}
                telah berhasil disimpan.
              </p>
              {renderSubmittedSummary(lastSubmittedData)}
              {!limitReached && sisaPengiriman > 0 && (
                <p className="text-xs text-gray-400 mt-3">
                  Anda masih dapat mengirim {sisaPengiriman} kali hari ini.
                </p>
              )}
            </div>
            <div className="px-6 pb-6">
              <button
                type="button"
                onClick={() => setShowSuccessModal(false)}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-4 rounded-xl transition-colors duration-200"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {showLimitModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden max-h-[90vh] overflow-y-auto">
            <div className="px-6 pt-8 pb-6 text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <TriangleAlert className="w-9 h-9 text-amber-600" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                Batas Pengiriman Tercapai
              </h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Pengiriman perawatan untuk kode{" "}
                <span className="font-semibold text-gray-700">{data?.kode}</span>{" "}
                hari ini sudah mencapai batas {MAX_PERAWATAN_AC_PER_HARI} kali.
              </p>
              {renderSubmittedSummary(lastSubmittedData)}
              <p className="text-xs text-gray-400 mt-3">
                Data terakhir yang berhasil dikirim hari ini ditampilkan di atas.
              </p>
            </div>
            <div className="px-6 pb-6">
              <button
                type="button"
                onClick={() => setShowLimitModal(false)}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-3 px-4 rounded-xl transition-colors duration-200"
              >
                Mengerti
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PerawatanAc;
